import { reactive } from 'vue';
import ContactContext from '@/contact/models/ContactContext';
import Dossier from '@/dossier/models/Dossier';
import AbstractContext from '@/shared/models/AbstractContext';
import { getById, mergeInto } from '@/shared/utils';
import { isPresent, present } from '@/shared/utils/typescript';
import Vehicle from '@/vehicle/models/Vehicle';
import JobCategory from '../constants/JobCategory';
import JobItemType from '../constants/JobItemType';
import JobType from '../constants/JobType';
import WarrantyStatus from '../constants/WarrantyStatus';
import { JobId } from './AbstractJob';
import Job, { JobApprovalData } from './Job';
import JobItem, { JobItemId } from './JobItem';
import JobMaster from './JobMaster';
import TimeRegistrationContainer from './TimeRegistrationContainer';
import DiagnosticTypeDescription from './diagnosis/DiagnosticTypeDescription';

export default class JobContext extends AbstractContext {
  jobs: Array<Job> = [];
  summaries: Array<JobMaster> = [];
  items: Array<JobItem> = [];
  timeRegistrationContainer: TimeRegistrationContainer | null = null;
  selectedJob: Job | null = null;
  diagnosticTypes: Array<DiagnosticTypeDescription> = [];

  constructor(
    readonly dossier: Dossier,
    readonly vehicle: Vehicle,
    readonly contactContext: ContactContext,
    readonly initialJobs?: Array<Job> | null,
  ) {
    super();
    if (initialJobs?.length !== 0 && initialJobs) {
      this.jobs = initialJobs;
    }
  }

  clone(hasInitialJobs = false): JobContext {
    const context = new JobContext(this.dossier, this.vehicle, this.contactContext, hasInitialJobs ? this.jobs : null);
    context.diagnosticTypes = this.diagnosticTypes;
    return context;
  }

  getJobById(jobId: JobId): Job | null {
    return this.jobs.find(j => j.id === jobId) ?? null;
  }

  getSelectedJob(): Job {
    return present(this.selectedJob);
  }

  getMechanicJobs(): Array<Job> {
    return this.jobs.filter(job => job.getCategory() === JobCategory.MECHANIC);
  }

  getQuotes(): Array<Job> {
    return this.jobs.filter(job => job.getCategory() === JobCategory.QUOTE);
  }

  getActivePartItems(): Array<JobItem> {
    return this.items.filter(i => i.type === JobItemType.PART && !i.deletedOn);
  }

  getItemById(id: JobItemId): JobItem | undefined {
    return getById(this.items, id);
  }

  getCommentSummaries(): Array<JobMaster> {
    return this.summaries.filter(j => j.type === JobType.COMMENT);
  }

  getJobSummaries(): Array<JobMaster> {
    return this.summaries.filter(j => j.type === JobType.JOB);
  }

  addSummary(job: JobMaster): void {
    mergeInto(job, this.summaries);
  }

  updatePrices(jobs: Array<Job>, warrantyStatuses: Array<WarrantyStatus>): void {
    jobs.forEach((job, i) => {
      job.warrantyStatus = warrantyStatuses[i] ?? job.warrantyStatus;
      job.original?.updateWith(job);
    });
  }

  updatePriceApprovals(jobs: Array<Job>, data: JobApprovalData): void {
    jobs.forEach(job => {
      job.priceApproval.update({ ...data, approvalStatus: job.priceApproval.status });
      if (job.original) job.original.priceApproval = job.priceApproval;
      const summary = this.getSummaryByJobId(job.id);
      if (summary) summary.priceApprovalStatus = job.priceApproval.status;
    });
  }

  updateOrderApprovals(jobs: Array<Job>, data: JobApprovalData): void {
    jobs.forEach(job => {
      job.order.update({ ...data, approvalStatus: job.order.status });
      if (job.original) job.original.order = job.order;
      const summary = this.getSummaryByJobId(job.id);
      if (summary) {
        summary.orderStatus = job.order.status;
        summary.hasOrderSignature = job.order.hasSignature;
      }
    });
  }

  getSummaryByJobId(jobId: JobId): JobMaster | undefined {
    return this.summaries.find(j => j.id === jobId);
  }

  isComment(jobId: JobId): boolean {
    return this.getSummaryByJobId(jobId)?.type === JobType.COMMENT;
  }

  initOriginals(originalContext: JobContext): void {
    this.jobs.forEach(job => {
      if (job.original) return;
      const originalJob = originalContext.getJobById(job.id);
      if (originalJob) job.setOriginal(originalJob);
    });
  }

  createEditContext(): JobContext {
    const editContext = reactive(this.clone()) as JobContext;
    editContext.jobs = this.jobs.filter(j => !j.isDeleted && !isPresent(j.severity) && j.getCategory() === JobCategory.MECHANIC).map(job => job.clone());
    editContext.jobs.forEach(j => j.initOrderStatus());
    return editContext;
  }
}